<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'WavoipConfiguration',
  components: {
    SettingsSection,
    LoadingState,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isWavoipEnabled: false,
      wavoipToken: '',
      externalToken: '',
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  watch: {
    inbox: {
      handler(newInbox) {
        if (newInbox && Object.keys(newInbox).length > 0) {
          this.loadSettings();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    loadSettings() {
      if (!this.inbox) return;

      this.isWavoipEnabled = Boolean(this.inbox.wavoip_enabled);
      this.wavoipToken = this.inbox.wavoip_token || '';
      this.externalToken = this.inbox.external_token || '';
    },

    async saveSettings() {
      if (this.isWavoipEnabled && !this.wavoipToken.trim()) {
        useAlert(this.$t('WAVOIP.CONFIGURATION.TOKEN_REQUIRED'));
        return;
      }

      this.isSaving = true;
      try {
        // Sincronizar external_token com wavoip_token
        const tokenValue = this.isWavoipEnabled ? this.wavoipToken.trim() : '';
        
        // Padronização seguindo a estrutura do Settings.vue
        const payload = {
          id: this.inbox.id,
          wavoip_enabled: this.isWavoipEnabled, // boolean - sem conversão
          wavoip_token: tokenValue,
          external_token: tokenValue, // ← Mesmo valor do wavoip_token
          channel: {
            wavoip_enabled: this.isWavoipEnabled, // boolean - sem conversão
            wavoip_token: tokenValue,
            external_token: tokenValue, // ← Mesmo valor do wavoip_token
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('WAVOIP.CONFIGURATION.SUCCESS'));
        this.loadSettings();
      } catch (error) {
        useAlert(
          error?.message ||
          error?.response?.data?.message ||
          this.$t('WAVOIP.CONFIGURATION.ERROR')
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isUpdating" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="saveSettings"
    >
      <SettingsSection
        :title="$t('WAVOIP.CONFIGURATION.TITLE')"
        :sub-title="$t('WAVOIP.CONFIGURATION.DESC')"
      >
        <div class="space-y-4">
          <div class="flex items-center space-x-2">
            <input
              id="wavoip-enable"
              type="checkbox"
              v-model="isWavoipEnabled"
              class="form-checkbox"
            />
            <label for="wavoip-enable" class="text-sm font-medium">
              {{ $t('WAVOIP.CONFIGURATION.ENABLE_LABEL') }}
            </label>
          </div>

          <div v-if="isWavoipEnabled">
            <label for="wavoip-token" class="block text-sm font-medium mb-1">
              {{ $t('WAVOIP.CONFIGURATION.TOKEN_LABEL') }}
            </label>
            <input
              id="wavoip-token"
              v-model.trim="wavoipToken"
              class="p-2 border border-gray-300 rounded w-full max-w-md"
              :placeholder="$t('WAVOIP.CONFIGURATION.PLACEHOLDER')"
              required
            />

            <p class="text-xs text-gray-500 mt-1">
              {{ $t('WAVOIP.CONFIGURATION.TOKEN_HELP') }}
            </p>
          </div>

          <div class="button-container space-x-2">
            <NextButton
              type="submit"
              :label="$t('WAVOIP.CONFIGURATION.SUBMIT')"
              :is-loading="isSaving"
              :disabled="isWavoipEnabled && !wavoipToken.trim()"
            />
          </div>
        </div>
      </SettingsSection>
    </form>
  </div>
</template>